// WeatherTicker.tsx
import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import logger from '../utils/logger';
import { getWeather } from '../API';

interface WeatherData {
  location: { name: string; country: string };
  current: {
    last_updated: string;
    temp_c: number;
    temp_f: number;
    condition: { text: string; icon: string };
    wind_kph: number;
    wind_dir: string;
    humidity: number;
    feelslike_c: number;
    feelslike_f: number;
  };
  forecast?: {
    forecastday: {
      day: {
        maxtemp_c: number;
        maxtemp_f: number;
        mintemp_c: number;
        mintemp_f: number;
        condition: { text: string };
        daily_chance_of_rain: number;
        totalprecip_mm: number;
      };
    }[];
  };
}

/** ----------------------------------------------------------------
 *  STYLES
 *  ---------------------------------------------------------------- */
const EDGE_FADE = 70; // px before each edge where the ticker starts to fade

/** 
 * We use CSS mask to fade the *content itself* out to 0 % opacity.
 *  - No colour is applied, so whatever sits behind the site will show through.
 *  - Works in all modern evergreen browsers (Chrome, Edge, Firefox, Safari).
 */
const TickerContainer = styled(Box)({
  width: '90%',
  marginLeft: '5%',
  position: 'relative',
  overflow: 'hidden',
  height: 24,
  // 🔑 Smooth fade to 0 % opacity at both ends
  WebkitMaskImage: `linear-gradient(to right,
      transparent 0px,
      black ${EDGE_FADE}px,
      black calc(100% - ${EDGE_FADE}px),
      transparent 100%)`,
  maskImage: `linear-gradient(to right,
      transparent 0px,
      black ${EDGE_FADE}px,
      black calc(100% - ${EDGE_FADE}px),
      transparent 100%)`,
});

const TickerScroll = styled(Box)({
  whiteSpace: 'nowrap',
  display: 'inline-block',
  animation: 'tickerScroll 30s linear infinite',
  '@keyframes tickerScroll': {
    '0%': { transform: 'translateX(0)' },
    '100%': { transform: 'translateX(-50%)' },
  },
});

const WeatherText = styled(Typography)({
  display: 'inline-block',
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 400,
  fontSize: 15,
  lineHeight: '19px',
  letterSpacing: '0.32px',
  textTransform: 'uppercase',
  padding: '3px 0',
  color: '#000',
});

/** ----------------------------------------------------------------
 *  COMPONENT
 *  ---------------------------------------------------------------- */
const WeatherTicker: React.FC = () => {
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /* ---------------- Fetch & cache weather ---------------- */
  useEffect(() => {
    const fetchWeather = async (latitude: number, longitude: number, isRetry = false) => {
      try {
        // Call the refactored getWeather function from API.ts
        const response = await getWeather(latitude, longitude, true);
        setWeatherData(response.data);
        setIsLoading(false);
      } catch (err) {
        logger.error('Weather fetch failed:', err);
        if (!isRetry) {
          // Retry with fallback – Charing Cross
          fetchWeather(51.5096831, -0.1258279, true);
        } else {
          setIsLoading(false);
        }
      }
    };

    // Get stored location or fall back to Charing Cross with error handling
    let lat = 51.5096831; // Default to Charing Cross
    let lon = -0.1258279;
    
    try {
      const storedLat = localStorage.getItem('userLatitude');
      const storedLon = localStorage.getItem('userLongitude');
      
      if (storedLat) lat = parseFloat(storedLat);
      if (storedLon) lon = parseFloat(storedLon);
    } catch (storageError) {
      logger.warn('Failed to read location from localStorage:', storageError);
      // Continue with default coordinates
    }
    
    fetchWeather(lat, lon);
  }, []);

  /* ---------------- Render ---------------- */
  if (isLoading) return null;

  const createTickerText = () => {
    if (!weatherData?.current?.condition) return ''.padEnd(200, ' ');
    const { text } = weatherData.current.condition;
    const currentTemp = weatherData.current.temp_c.toFixed(1);
    let forecast = '';

    if (weatherData.forecast?.forecastday?.length) {
      const today = weatherData.forecast.forecastday[0].day;
      const max = Math.round(today.maxtemp_c);
      const min = Math.round(today.mintemp_c);
      const rain = today.daily_chance_of_rain;
      forecast = `TODAY: ${min}°C-${max}°C${
        rain >= 30 ? ` – ${rain}% CHANCE OF RAIN` : ''
      }`;
    }

    const segment = `${text.toUpperCase()} ${currentTemp}°C${
      forecast ? ` | ${forecast}` : ''
    } |     `;
    return segment.repeat(8);
  };

  return (
    <TickerContainer>
      <TickerScroll>
        <WeatherText>{createTickerText()}</WeatherText>
      </TickerScroll>
    </TickerContainer>
  );
};

export default WeatherTicker;